import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GOLD = '#C4973A';
const CREAM = '#FFFBF5';

/**
 * Fixed-position speech bubble anchored to a price input via getBoundingClientRect.
 * Tail points down at the input; dismisses on outside click or any scroll.
 */
// `message` (optional) replaces the default body line — used by qualified
// above-range warnings where no trustworthy "average price" claim exists.
function HighPriceWarningPopover({ anchorRef, visible, fairPriceLabel, message, onDismiss }) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [fadedIn, setFadedIn] = useState(false);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) {
      setPosition(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPosition({
      centerX: rect.left + rect.width / 2,
      // Wrapper bottom sits here; tail tip lands ~10px above the input top edge.
      top: rect.top - 10,
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!visible) {
      setPosition(null);
      setFadedIn(false);
      return undefined;
    }
    updatePosition();
    const raf = requestAnimationFrame(() => setFadedIn(true));
    window.addEventListener('resize', updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleScroll = () => onDismiss();

    const handlePointerDown = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      onDismiss();
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [visible, onDismiss]);

  if (!visible || !position) return null;

  return createPortal(
      <div
        ref={popoverRef}
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          left: position.centerX,
          top: position.top,
          transform: 'translate(-50%, -100%)',
          zIndex: 10000,
          maxWidth: 300,
          width: 'max-content',
          minWidth: 240,
          pointerEvents: 'auto',
          fontFamily: 'Nunito, sans-serif',
          opacity: fadedIn ? 1 : 0,
          transition: 'opacity 250ms ease-out',
        }}
      >
        <div
          style={{
            backgroundColor: CREAM,
            border: `1.5px solid ${GOLD}`,
            borderRadius: 12,
            padding: '12px 14px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: '#374151',
              lineHeight: 1.35,
              marginBottom: 4,
            }}
          >
            Heads up — this may be harder to trade or sell.
          </div>
          <div
            style={{
              fontWeight: 400,
              fontSize: 13,
              color: '#6B7280',
              lineHeight: 1.4,
            }}
          >
            {message || <>The average price for this pedal in this condition is {fairPriceLabel}.</>}
          </div>
        </div>
        {/* Downward tail — gold outline + cream fill */}
        <div
          style={{
            position: 'relative',
            width: 16,
            height: 8,
            margin: '0 auto',
            marginTop: -1,
          }}
          aria-hidden="true"
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: `8px solid ${GOLD}`,
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${CREAM}`,
            }}
          />
        </div>
      </div>,
    document.body
  );
}

export default HighPriceWarningPopover;
